/* Audit chain integrity, on the screen an operator already watches during a deployment.
 *
 * **The three outcomes stay three.** `pending`, `no_data` and a verdict render differently and are
 * never collapsed. A UI that showed an unfinished check as a tick would report an unchecked chain as
 * sound; one that showed an empty day as a tick would do the same for a pruned partition. `sound` is
 * the only thing that earns green, and it is true for `verified` alone.
 *
 * **What this screen must not claim.** Verification proves *order* — unbroken linkage, no altered
 * entry, no removed middle, no truncated tail. It does not prove *origin*: a well-formed continuation
 * appended by whatever holds the INSERT grant is what a hash chain permits by construction. The
 * footnote says so, because a green tick with no caveat is how an operator comes to believe the
 * stronger claim.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_CHAIN_DATE,
  useChainVerification,
  useVerifyChain,
} from '../api/useChainVerification';
import type { ChainVerification as Verification } from '@/shared/api/generated/audit';

/* Green for `verified` only. Amber for `no_data` — not grey, because "we have no evidence for this
 * day" is a thing to look into rather than a neutral fact, and under partition retention it is as
 * likely to be a pruned day as a quiet one. */
function outcomeColour(outcome: Verification['verified']): string {
  switch (outcome) {
    case 'verified':
      return 'var(--status-ok)';
    case 'no_data':
      return 'var(--status-warn)';
    case 'diverged':
      return 'var(--status-error)';
  }
}

function Outcome({ result }: { readonly result: Verification }): ReactElement {
  const { t } = useTranslation();
  return (
    <div>
      <p
        style={{
          margin: '0 0 var(--space-1)',
          color: outcomeColour(result.verified),
          fontWeight: 600,
        }}
      >
        {t(`audit.outcome.${result.verified}`)}
      </p>
      <p
        style={{
          margin: '0 0 var(--space-2)',
          color: 'var(--text-secondary)',
          fontSize: 'var(--text-xs)',
        }}
      >
        {t('audit.examined', { count: result.entries_examined })}
      </p>

      {/* A divergence names the record. "The chain is invalid" is unactionable; a sequence and two
       * hashes is what an auditor works from. */}
      {result.divergence !== null && (
        <dl style={{ margin: 0, fontSize: 'var(--text-xs)' }}>
          <dt style={{ color: 'var(--text-secondary)' }}>
            {t('audit.divergence.sequence')}
          </dt>
          <dd
            style={{ margin: '0 0 var(--space-2)', fontFamily: 'var(--lumiq-font-mono)' }}
          >
            {result.divergence.sequence}
          </dd>
          <dt style={{ color: 'var(--text-secondary)' }}>
            {t('audit.divergence.detail')}
          </dt>
          <dd style={{ margin: '0 0 var(--space-2)' }}>{result.divergence.detail}</dd>
          <dt style={{ color: 'var(--text-secondary)' }}>
            {t('audit.divergence.expected')}
          </dt>
          <dd
            style={{
              margin: '0 0 var(--space-2)',
              fontFamily: 'var(--lumiq-font-mono)',
              wordBreak: 'break-all',
            }}
          >
            {result.divergence.expected_hash}
          </dd>
          <dt style={{ color: 'var(--text-secondary)' }}>
            {t('audit.divergence.stored')}
          </dt>
          <dd
            style={{
              margin: 0,
              fontFamily: 'var(--lumiq-font-mono)',
              wordBreak: 'break-all',
            }}
          >
            {result.divergence.stored_hash}
          </dd>
        </dl>
      )}

      {/* Truncation. Rendered separately from a divergence because nothing in the arithmetic broke —
       * the walk was clean and the *head* disagrees about how far the day got. A caller that only
       * checked `divergence` would call this day verified. */}
      {result.pointer_disagreement !== '' && (
        <p
          style={{
            margin: 'var(--space-1) 0 0',
            padding: 'var(--space-2)',
            border: '1px solid var(--status-error)',
            borderRadius: 'var(--lumiq-radius)',
            fontSize: 'var(--text-xs)',
          }}
          role="alert"
        >
          {result.pointer_disagreement}
        </p>
      )}
    </div>
  );
}

export function ChainVerification(): ReactElement {
  const { t } = useTranslation();
  const [chainDate] = useState(DEFAULT_CHAIN_DATE);
  const { data, error } = useChainVerification(chainDate);
  const verify = useVerifyChain(chainDate);

  return (
    <section style={{ marginTop: 'var(--space-5)' }}>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-md)' }}>{t('audit.title')}</h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
          {chainDate}
        </span>
      </header>

      <button
        type="button"
        onClick={() => {
          verify.mutate();
        }}
        disabled={verify.isPending}
        style={{
          marginTop: 'var(--space-2)',
          padding: 'var(--space-1) var(--space-2)',
          borderRadius: 'var(--lumiq-radius)',
          border: '1px solid var(--badge-border)',
          background: 'transparent',
          color: 'var(--text-primary)',
          cursor: verify.isPending ? 'default' : 'pointer',
          fontSize: 'var(--text-xs)',
        }}
      >
        {t('audit.verify')}
      </button>

      {/* The 503 an operator gets on a deployment with no broker. Shown with the server's own detail
       * rather than a generic failure, because "no Celery broker is configured" is actionable and
       * "something went wrong" is not. */}
      {verify.error !== null && (
        <p
          style={{
            margin: 'var(--space-2) 0 0',
            color: 'var(--status-warn)',
            fontSize: 'var(--text-xs)',
          }}
          role="alert"
        >
          {verify.error.message}
        </p>
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>
        {error !== null && (
          <p
            style={{
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-xs)',
            }}
          >
            {t('audit.unreadable')}
          </p>
        )}
        {error === null && data?.result === null && (
          /* Deliberately not a tick and deliberately not a cross. Nothing has been verified, and both
           * of the other renderings would be a claim. */
          <p
            style={{
              margin: 0,
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-xs)',
            }}
          >
            {data.finished
              ? t('audit.no_result', { detail: data.detail ?? '' })
              : t('audit.running')}
          </p>
        )}
        {error === null && data?.result != null && <Outcome result={data.result} />}
      </div>

      <p
        style={{
          margin: 'var(--space-3) 0 0',
          color: 'var(--text-secondary)',
          fontSize: 'var(--text-xs)',
        }}
      >
        {t('audit.proves_order_not_origin')}
      </p>
    </section>
  );
}
